
import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, inject, injectable } from 'inversify'
import { Api } from '@evertel/api'

const IS_EMAIL_VERIFIED_POLL_INTERVAL_MS = 2000 // 2 seconds

// BroadcastChannel constants for cross-tab communication
export const BC_CHANNEL_NAME = 'evertel-email-verification'
export const BC_MESSAGES = {
    PING: 'ping',
    PONG_REGISTRATION: 'pong-registration',
    PONG_EMAIL_CHANGE: 'pong-email-change',
    VERIFIED: 'verified'
} as const

export type BCMessageType = typeof BC_MESSAGES[keyof typeof BC_MESSAGES]

class EmailVerificationController {
    email = ''
    isVerified = false
    private getIsVerifiedPollInterval: ReturnType<typeof setInterval> | null = null

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init = (email: string): void => {
        this.isVerified = false
        this.email = email?.toLowerCase()

        this.startGetIsVerifiedPoll()
    }

    resendVerificationEmail = async (): Promise<void> => {

        if (!this.email) {
            throw new Error('Please provide an email')
        }

        await this.api.Routes.Auth0.postResendVerificationEmail(this.email)
    }

    getIsVerified = async (): Promise<boolean> => {

        if (!this.email) {
            throw new Error('Please provide an email')
        }

        interface Auth0DataIsVerified {
            email_verified: boolean
        }

        // our API is a pass-through to auth0
        const response: Auth0DataIsVerified[] = await this.api.Routes.Auth0.getIsVerified(this.email) as Auth0DataIsVerified[]

        const verified = response[0]?.email_verified || false

        runInAction(() => {
            this.isVerified = verified
        })

        // Stop polling once verified
        if (verified) {
            this.stopGetIsVerifiedPoll()
        }

        return verified
    }

    startGetIsVerifiedPoll = (): void => {
        // stop poll first in case it was running so we don't create duplicates
        this.stopGetIsVerifiedPoll()

        // create poll
        this.getIsVerifiedPollInterval = setInterval(() => {
            this.getIsVerified().catch(() => {/* no-op */})

        }, IS_EMAIL_VERIFIED_POLL_INTERVAL_MS)
    }

    stopGetIsVerifiedPoll = (): void => {
        if (this.getIsVerifiedPollInterval) {
            clearInterval(this.getIsVerifiedPollInterval)
            this.getIsVerifiedPollInterval = null
        }
    }
}

decorate(injectable(), EmailVerificationController)
decorate(inject(Api), EmailVerificationController, 0)

export { EmailVerificationController }
